// [CurveBeam] - [BoneVue]

import axios, { AxiosError } from 'axios';
import { BaseApiResponse } from './dtos/base-api-response.dto';
import { ScanDetail } from '../models/scan.interface';
import { BaseScanRequest } from './dtos/scan.dto';
import { GetMeasurementsResponse } from './dtos/measurement.dto';
import { GenerateRoiProjectionRequest, GetMeasurementsRequest, RotateRequest } from './dtos/result.dto';

export class BmdApiService {
  private baseUrl = 'http://localhost:3002/api/bmd';

  private static instance: BmdApiService;

  static getInstance(): BmdApiService {
    if (!BmdApiService.instance) {
      BmdApiService.instance = new BmdApiService();
    }

    return BmdApiService.instance;
  }

  setBackendUrl(url: string) {
    this.baseUrl = url;
  }

  private getAccessToken() {
    let authData: any = localStorage.getItem('ls.authData');
    if (!authData) {
      throw new Error('No auth data found');
    }
    authData = JSON.parse(authData);
    return authData.accessToken;
  }

  private buildUrl(path: string) {
    return `${this.baseUrl}/BoneVue/${path}`;
  }

  private getDefaultHeaders() {
    return {
      Authorization: `Bearer ${this.getAccessToken()}`,
    };
  }

  async getScanDetails(request: BaseScanRequest): Promise<ScanDetail> {
    const response = await axios
      .post<BaseApiResponse<ScanDetail>>(
        this.buildUrl('getScanDetails'),
        request,
        { headers: this.getDefaultHeaders() },
      )
      .catch(this.handleErrorResponse);

    return response.data.data;
  }

  async rotate(request: RotateRequest): Promise<ScanDetail> {
    const response = await axios
      .post<BaseApiResponse<ScanDetail>>(
        this.buildUrl('rotate'),
        request,
        { headers: this.getDefaultHeaders() },
      )
      .catch(this.handleErrorResponse);

    return response.data.data;
  }

  async getMeasurements(request: GetMeasurementsRequest): Promise<GetMeasurementsResponse[]> {
    const response = await axios
      .post<GetMeasurementsResponse[]>(this.buildUrl('getMeasurements'), request,
        { headers: this.getDefaultHeaders() },
      )
      .catch(this.handleErrorResponse);

    return response.data;
  }

  // eslint-disable-next-line
  async saveMeasurement(studyId: string, seriesId: string, part10Buffer: any): Promise<string> {
    const response = await axios
      .post<string>(this.buildUrl(`saveMeasurement/${studyId}/${seriesId}`), part10Buffer, {
        headers:
          {
            ...this.getDefaultHeaders(),
            'Content-Type': 'application/octet-stream',
          },
      })
      .catch(this.handleErrorResponse);

    return response.data;
  }

  // eslint-disable-next-line
  async saveSegmentation(request: GenerateRoiProjectionRequest, preSignedUrl: string, part10Buffer: any): Promise<ScanDetail> {
    await this.uploadToS3(preSignedUrl, part10Buffer);

    const response = await axios
      .post<ScanDetail>(
        this.buildUrl(`generateRoiProjection`),
        request,
        { headers: this.getDefaultHeaders() },
      )
      .catch(this.handleErrorResponse);

    return response.data;
  }

  private handleErrorResponse(error: AxiosError): never {
    const { response } = error;

    if (!response) {
      throw new Error(
        'Cannot connect to server, please refresh the page and try again',
      );
      throw new Error('Cannot connect to server, please refresh the page and try again');
    }

    throw new Error(response.data.message);
  }

  private async uploadToS3(preSignedUrl: string, byteArray: any) {
    try {
      const response = await axios.put(preSignedUrl, byteArray, {
        headers: {
          'Content-Type': 'application/octet-stream', // Use appropriate content type for binary data
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${progress}%`);
        },
      });

      if (response.status === 200) {
        console.log('Upload successful!');
      } else {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
    } catch (error) {
      throw new Error(`Error uploading to S3: ${error}`);
    }
  }
}
